/**
 * Settings Page - US-075: Mobile Responsive Design
 *
 * This page handles all application settings with fully responsive layouts:
 * - Mobile (<768px): Horizontal scrollable tabs, stacked forms, full-width buttons
 * - Tablet (768px-1024px): Collapsible sidebar, 2-column forms
 * - Desktop (>1024px): Full sidebar with descriptions, multi-column layouts
 *
 * All interactive elements maintain minimum 44px touch targets for accessibility.
 */
import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Settings as SettingsIcon,
  Database,
  Zap,
  Bell,
  Shield,
  Save,
  Copy,
  Check,
  RefreshCw,
  ExternalLink,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  Brain,
  Webhook,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Layers,
  Clock,
  AlertTriangle,
  Search,
  Inbox,
  Activity,
  Users,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { api } from '../api/client';
import { useSystemHealth, useDeadLetterEntries, useDeadLetterStats, useResolveDeadLetter, useModelCatalog } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { TeamSettings } from './settings/TeamSettings';
import { SearchInput } from '../components/ui/SearchInput';
import { FilterDropdown, type FilterOption } from '../components/ui/FilterDropdown';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardHeader, CardBody, CardFooter } from '../components/ui/Card';
import { Alert } from '../components/ui/Alert';
import { Badge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import { ModelPillSelector } from '../components/ModelPillSelector';
import { ThresholdStepper } from '../components/ThresholdStepper';
import AIProviders from '../components/settings/AIProviders';
import { OPENROUTER_MODEL_IDS } from '../constants/openrouter-models';
import type { ConfigResponse, ConfigData, AITier, DeadLetterResolvedStatus } from '../types';

interface SettingsSection {
  id: string;
  name: string;
  icon: React.ElementType;
  description: string;
}

// US-076: Model catalog from intelligent-router.ts MODEL_CATALOG
// Each entry has display info and pricing for the admin UI
interface ModelCatalogEntry {
  id: string;
  displayName: string;
  provider: string;
  inputCostPer1M: number;
  outputCostPer1M: number;
  tier: number; // 1=free, 2=cheap, 3=quality, 4=premium
  isFree: boolean;
  location: string;
  contextWindow: number;
  thinkingMode: 'none' | 'optional' | 'required';
  warning?: string;
}

const MODEL_CATALOG: ModelCatalogEntry[] = [
  // Both free-tier entries that used to be listed here (llama-3.3-70b-instruct
  // and gemma-3n-e4b-it, both the OpenRouter ":free" variant) were removed
  // 2026-10-22/26 — confirmed retired upstream (absent from OpenRouter's live
  // /api/v1/models). Leaving them pickable here offered admins a model that
  // silently 404s. No live free-tier replacement was added back; see the
  // comment above MODEL_CATALOG in intelligent-router.ts for why.
  {
    id: OPENROUTER_MODEL_IDS.gpt6Luna,
    displayName: 'GPT-6 Luna',
    provider: 'OpenAI',
    inputCostPer1M: 0.10,
    outputCostPer1M: 0.50,
    tier: 2,
    isFree: false,
    location: 'us',
    contextWindow: 1_050_000,
    thinkingMode: 'none',
  },
  {
    id: OPENROUTER_MODEL_IDS.deepseekChat,
    displayName: 'DeepSeek Chat V3',
    provider: 'DeepSeek',
    inputCostPer1M: 0.14,
    outputCostPer1M: 0.28,
    tier: 2,
    isFree: false,
    location: 'china',
    contextWindow: 128_000,
    thinkingMode: 'none',
  },
  {
    id: OPENROUTER_MODEL_IDS.gpt4oMini,
    displayName: 'GPT-4o Mini',
    provider: 'OpenAI',
    inputCostPer1M: 0.15,
    outputCostPer1M: 0.60,
    tier: 2,
    isFree: false,
    location: 'us',
    contextWindow: 128_000,
    thinkingMode: 'none',
  },
  {
    id: OPENROUTER_MODEL_IDS.claudeHaiku45,
    displayName: 'Claude Haiku 4.5',
    provider: 'Anthropic',
    inputCostPer1M: 1.00,
    outputCostPer1M: 5.00,
    tier: 3,
    isFree: false,
    location: 'us',
    contextWindow: 200_000,
    thinkingMode: 'none',
  },
  // DeepSeek's "reasoner" (thinking-token) tier removed 2026-08-06 — its OpenRouter id was invalid, and it has no OpenRouter equivalent
  {
    id: OPENROUTER_MODEL_IDS.gpt4o,
    displayName: 'GPT-4o',
    provider: 'OpenAI',
    inputCostPer1M: 2.50,
    outputCostPer1M: 10.00,
    tier: 4,
    isFree: false,
    location: 'us',
    contextWindow: 128_000,
    thinkingMode: 'none',
  },
  {
    id: OPENROUTER_MODEL_IDS.claudeSonnet5,
    displayName: 'Claude Sonnet 5',
    provider: 'Anthropic',
    inputCostPer1M: 3.00,
    outputCostPer1M: 15.00,
    tier: 4,
    isFree: false,
    location: 'us',
    contextWindow: 200_000,
    thinkingMode: 'none',
  },
];

// Helper to format cost for display
function formatCost(costPer1M: number): string {
  if (costPer1M === 0) return 'Free';
  if (costPer1M < 1) return `$${costPer1M.toFixed(2)}`;
  return `$${costPer1M.toFixed(2)}`;
}

// Helper to get model info from catalog (also checks for models not in catalog)
function getModelInfo(modelId: string): ModelCatalogEntry | undefined {
  return MODEL_CATALOG.find(m => m.id === modelId);
}

// Tier badge color mapping
const TIER_COLORS: Record<number, { bg: string; text: string; label: string }> = {
  1: { bg: 'bg-green-100', text: 'text-green-700', label: 'Free' },
  2: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Cheap' },
  3: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Quality' },
  4: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Premium' },
};

// US-049: SmartRecruiters candidate stages for AI review eligibility.
// OFFER and HIRED are excluded - AI review is not relevant at those stages.
// REJECTED and WITHDRAWN are excluded - candidates in terminal states don't need review.
const SR_CANDIDATE_STAGES = {
  NEW: { value: 'NEW', label: 'New', description: 'Newly applied (recommended)' },
  IN_REVIEW: { value: 'IN_REVIEW', label: 'In Review', description: 'Being screened by recruiter' },
  INTERVIEW: { value: 'INTERVIEW', label: 'Interview', description: 'In interview process' },
} as const;

const settingsSections: SettingsSection[] = [
  {
    id: 'system-health',
    name: 'System Health',
    icon: Activity,
    description: 'Infrastructure, queues & error queue',
  },
  {
    id: 'integrations',
    name: 'Integrations',
    icon: Zap,
    description: 'SmartRecruiters and AI settings',
  },
  {
    id: 'ai-providers',
    name: 'AI Providers',
    icon: Brain,
    description: 'Provider keys, enablement, and model assignments',
  },
  {
    id: 'webhook',
    name: 'Webhook',
    icon: Webhook,
    description: 'Webhook URL and setup',
  },
  {
    id: 'general',
    name: 'General',
    icon: SettingsIcon,
    description: 'Basic application settings',
  },
  {
    id: 'retention',
    name: 'Data Retention',
    icon: Clock,
    description: 'GDPR/CCPA data retention policy',
  },
  {
    id: 'notifications',
    name: 'Notifications',
    icon: Bell,
    description: 'Email and alert preferences',
  },
  {
    id: 'security',
    name: 'Security',
    icon: Shield,
    description: 'Authentication and access control',
  },
  {
    id: 'team',
    name: 'Team',
    icon: Users,
    description: 'Manage team members and invitations',
  },
];

export default function Settings() {
  const location = useLocation();
  const initialTab = (location.state as { tab?: string } | null)?.tab || 'integrations';
  const [activeSection, setActiveSection] = useState(initialTab);
  const [config, setConfig] = useState<ConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // US-075: Mobile dropdown state for section navigation
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Update active section when navigated with state
  useEffect(() => {
    const tab = (location.state as { tab?: string } | null)?.tab;
    if (tab && settingsSections.some(s => s.id === tab)) {
      setActiveSection(tab);
    }
  }, [location.state]);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getConfig();
      setConfig(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // US-237: Prefetch model catalog on Settings mount so IntegrationsSettings gets it from cache (Epic 34)
  useModelCatalog();

  // US-075: Get current section for mobile dropdown display
  const currentSection = settingsSections.find((s) => s.id === activeSection);

  const { canManageUsers } = useAuth();
  const visibleSections = settingsSections.filter(
    (s) => s.id !== 'team' || canManageUsers
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Spinner size="lg" />
        <span className="text-sm text-gray-500">Loading configuration...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error" title="Failed to load configuration">
        {error}
        {/* US-075: Full-width retry button on mobile */}
        <Button variant="secondary" size="sm" onClick={fetchConfig} className="mt-3 w-full sm:w-auto">
          <RefreshCw className="mr-2 h-4 w-4" />
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
      {/* US-075: Mobile Navigation - Dropdown selector for screens < 768px */}
      <div className="lg:hidden">
        <button
          onClick={() => setMobileNavOpen(!mobileNavOpen)}
          className="w-full flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-left shadow-sm min-h-[48px] active:bg-gray-50"
          aria-expanded={mobileNavOpen}
          aria-haspopup="listbox"
        >
          <div className="flex items-center gap-3">
            {currentSection && <currentSection.icon className="h-5 w-5 text-gray-500" />}
            <div>
              <p className="font-medium text-gray-900">{currentSection?.name}</p>
              <p className="text-xs text-gray-500">{currentSection?.description}</p>
            </div>
          </div>
          <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${mobileNavOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* US-075: Mobile dropdown menu */}
        {mobileNavOpen && (
          <div className="mt-2 rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
            {visibleSections.map((section) => (
              <button
                key={section.id}
                onClick={() => {
                  setActiveSection(section.id);
                  setMobileNavOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors min-h-[48px] ${
                  activeSection === section.id
                    ? 'bg-primary-50 text-primary-700 border-l-4 border-primary-500'
                    : 'text-gray-600 hover:bg-gray-50 border-l-4 border-transparent'
                }`}
              >
                <section.icon className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="font-medium">{section.name}</p>
                  <p className="text-xs text-gray-500">{section.description}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* US-075: Tablet/Desktop Sidebar - Horizontal tabs on tablet, vertical nav on desktop */}
      <div className="hidden lg:block w-64 flex-shrink-0">
        <nav className="flex flex-col gap-1 sticky top-4">
          {visibleSections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex items-center gap-3 rounded-lg px-4 py-3 text-left text-sm transition-colors w-full min-h-[48px] ${
                activeSection === section.id
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <section.icon className="h-5 w-5 flex-shrink-0" />
              <div>
                <p className="font-medium">{section.name}</p>
                <p className="text-xs text-gray-500">{section.description}</p>
              </div>
            </button>
          ))}
        </nav>
      </div>

      {/* US-075: Tablet Horizontal Tabs - Scrollable tabs for medium screens (768px-1024px) */}
      <div className="hidden md:flex lg:hidden overflow-x-auto -mx-4 px-4 pb-2 gap-2 scrollbar-hide">
        {visibleSections.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm transition-colors whitespace-nowrap flex-shrink-0 min-h-[44px] ${
              activeSection === section.id
                ? 'bg-primary-50 text-primary-700 font-medium'
                : 'text-gray-600 hover:bg-gray-50 bg-white border border-gray-200'
            }`}
          >
            <section.icon className="h-4 w-4 flex-shrink-0" />
            <span>{section.name}</span>
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="flex-1 min-w-0">
        {activeSection === 'system-health' && <SystemHealthSettings />}
        {activeSection === 'integrations' && config && (
          <IntegrationsSettings config={config} onRefresh={fetchConfig} />
        )}
        {activeSection === 'ai-providers' && <AIProviders />}
        {activeSection === 'webhook' && config && (
          <WebhookSettings config={config} onRefresh={fetchConfig} />
        )}
        {activeSection === 'general' && <GeneralSettings />}
        {activeSection === 'retention' && config && (
          <DataRetentionSettings config={config} onRefresh={fetchConfig} />
        )}
        {activeSection === 'notifications' && <NotificationsSettings />}
        {activeSection === 'security' && <SecuritySettings />}
        {activeSection === 'team' && <TeamSettings />}
      </div>
    </div>
  );
}

// US-076: Reusable Tier Model Card component for model selection
interface TierModelCardProps {
  tierKey: string;
  tierLabel: string;
  tierBadge: string;
  tierDescription: string;
  borderColor: string;
  bgColor: string;
  badgeBg: string;
  badgeText: string;
  focusColor: string;
  value: string;
  onChange: (value: string) => void;
  models: ModelCatalogEntry[];
  isDefault?: boolean;
  extraClasses?: string;
}

function TierModelCard({
  tierLabel,
  tierBadge,
  tierDescription,
  borderColor,
  bgColor,
  badgeBg,
  badgeText,
  focusColor,
  value,
  onChange,
  models,
  isDefault,
  extraClasses = '',
}: TierModelCardProps) {
  const selectedModel = getModelInfo(value);
  const isCustomModel = !selectedModel && value;

  return (
    <div className={`p-4 rounded-lg border ${borderColor} ${bgColor} ${extraClasses}`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        <span className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${badgeBg} ${badgeText} text-xs font-bold`}>
            {tierBadge}
          </span>
          {tierLabel}
          {isDefault && <Badge variant="info" size="sm">Default</Badge>}
        </span>
        <span className="block mt-1 text-xs font-normal text-gray-500">{tierDescription}</span>
      </label>

      {/* US-076: Model selector with all catalog models */}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm ${focusColor} focus:outline-none focus:ring-2 min-h-[44px]`}
      >
        {/* Group models by tier level */}
        <optgroup label="Free Tier">
          {models.filter(m => m.tier === 1).map(m => (
            <option key={m.id} value={m.id}>
              {m.displayName} - Free
            </option>
          ))}
        </optgroup>
        <optgroup label="Budget Tier ($0.10-$0.60/1M tokens)">
          {models.filter(m => m.tier === 2).map(m => (
            <option key={m.id} value={m.id}>
              {m.displayName} - In: {formatCost(m.inputCostPer1M)} / Out: {formatCost(m.outputCostPer1M)}
            </option>
          ))}
        </optgroup>
        <optgroup label="Quality Tier ($1.00-$5.00/1M tokens)">
          {models.filter(m => m.tier === 3).map(m => (
            <option key={m.id} value={m.id}>
              {m.displayName} - In: {formatCost(m.inputCostPer1M)} / Out: {formatCost(m.outputCostPer1M)}
              {m.warning ? ' (!)' : ''}
            </option>
          ))}
        </optgroup>
        <optgroup label="Premium Tier ($2.50-$15.00/1M tokens)">
          {models.filter(m => m.tier === 4).map(m => (
            <option key={m.id} value={m.id}>
              {m.displayName} - In: {formatCost(m.inputCostPer1M)} / Out: {formatCost(m.outputCostPer1M)}
            </option>
          ))}
        </optgroup>
        {/* Show current value if it's a custom model not in catalog */}
        {isCustomModel && (
          <optgroup label="Custom">
            <option value={value}>{value} (custom)</option>
          </optgroup>
        )}
      </select>

      {/* US-076: Cost and info summary below dropdown */}
      <div className="mt-2 space-y-1">
        {selectedModel ? (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${TIER_COLORS[selectedModel.tier]?.bg || 'bg-gray-100'} ${TIER_COLORS[selectedModel.tier]?.text || 'text-gray-700'}`}>
                {TIER_COLORS[selectedModel.tier]?.label || `Tier ${selectedModel.tier}`}
              </span>
              <span className="text-xs text-gray-500">{selectedModel.provider}</span>
              {selectedModel.location !== 'us' && selectedModel.location !== 'global' && (
                <span className="text-xs text-amber-600">
                  {selectedModel.location.toUpperCase()} region
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500">
              {selectedModel.isFree ? (
                'No cost per token'
              ) : (
                <>
                  Input: ${selectedModel.inputCostPer1M.toFixed(2)}/1M tokens
                  {' | '}
                  Output: ${selectedModel.outputCostPer1M.toFixed(2)}/1M tokens
                </>
              )}
              {' | '}
              {(selectedModel.contextWindow / 1000).toFixed(0)}k context
            </p>
            {selectedModel.warning && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                {selectedModel.warning}
              </p>
            )}
          </>
        ) : isCustomModel ? (
          <p className="text-xs text-gray-500">
            Custom model ID (not in catalog). Verify it exists on OpenRouter.
          </p>
        ) : (
          <p className="text-xs text-gray-500">Select a model for this tier.</p>
        )}
      </div>
    </div>
  );
}

// US-078: Task-to-Tier Table with model display names and cost info
interface TaskTierTableProps {
  taskTierEmbedding: AITier;
  setTaskTierEmbedding: (v: AITier) => void;
  taskTierExtraction: AITier;
  setTaskTierExtraction: (v: AITier) => void;
  taskTierComparison: AITier;
  setTaskTierComparison: (v: AITier) => void;
  taskTierReasoning: AITier;
  setTaskTierReasoning: (v: AITier) => void;
  taskTierAudit: AITier;
  setTaskTierAudit: (v: AITier) => void;
  aiModelTierFast: string;
  aiModelTierStandard: string;
  aiModelTierPremium: string;
}

function TaskTierTable({
  taskTierEmbedding, setTaskTierEmbedding,
  taskTierExtraction, setTaskTierExtraction,
  taskTierComparison, setTaskTierComparison,
  taskTierReasoning, setTaskTierReasoning,
  taskTierAudit, setTaskTierAudit,
  aiModelTierFast, aiModelTierStandard, aiModelTierPremium,
}: TaskTierTableProps) {
  // Helper to resolve model ID for a tier
  const getModelForTier = (tier: AITier): string => {
    switch (tier) {
      case 'fast': return aiModelTierFast;
      case 'standard': return aiModelTierStandard;
      case 'premium': return aiModelTierPremium;
    }
  };

  // Helper to display model name with cost
  const renderModelCell = (tier: AITier) => {
    const modelId = getModelForTier(tier);
    const info = getModelInfo(modelId);
    if (info) {
      return (
        <div>
          <div className="text-sm text-gray-700">{info.displayName}</div>
          <div className="text-xs text-gray-400">
            {info.isFree ? 'Free' : `$${info.inputCostPer1M.toFixed(2)} / $${info.outputCostPer1M.toFixed(2)} per 1M`}
          </div>
        </div>
      );
    }
    return <span className="text-sm text-gray-500">{modelId}</span>;
  };

  const tasks: Array<{
    label: string;
    description: string;
    value: AITier;
    onChange: (v: AITier) => void;
  }> = [
    { label: 'Embedding Generation', description: 'Vector embeddings for similarity search', value: taskTierEmbedding, onChange: setTaskTierEmbedding },
    { label: 'Resume Extraction', description: 'Extract data from resume text', value: taskTierExtraction, onChange: setTaskTierExtraction },
    { label: 'Candidate Comparison', description: 'Compare against job requirements', value: taskTierComparison, onChange: setTaskTierComparison },
    { label: 'Deep Reasoning', description: 'Complex multi-step analysis', value: taskTierReasoning, onChange: setTaskTierReasoning },
    { label: 'Quality Audit', description: 'Validate AI assessments', value: taskTierAudit, onChange: setTaskTierAudit },
  ];

  return (
    <div className="overflow-x-auto -mx-4 sm:mx-0">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task Type</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-32">Tier</th>
            <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {tasks.map((task) => (
            <tr key={task.label} className="hover:bg-gray-50">
              <td className="px-4 py-4">
                <div className="text-sm font-medium text-gray-900">{task.label}</div>
                <div className="text-xs text-gray-500 hidden sm:block">{task.description}</div>
              </td>
              <td className="px-4 py-4">
                <select
                  value={task.value}
                  onChange={(e) => task.onChange(e.target.value as AITier)}
                  className="block w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="fast">Fast</option>
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                </select>
              </td>
              <td className="hidden md:table-cell px-4 py-4">
                {renderModelCell(task.value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface IntegrationsSettingsProps {
  config: ConfigResponse;
  onRefresh: () => void;
}

// US-078: Task-to-Tier mapping definitions (prepared for future task routing UI)
// These mappings can be used to display task-specific tier configuration in an advanced settings section
const _TASK_TIER_MAPPINGS = [
  {
    task: 'embedding',
    label: 'Embedding Generation',
    description: 'Generate vector embeddings for similarity search',
    configKey: 'task_tier_embedding' as keyof ConfigData,
    defaultTier: 'fast' as AITier,
  },
  {
    task: 'extraction',
    label: 'Resume Extraction',
    description: 'Extract structured data from resume text',
    configKey: 'task_tier_extraction' as keyof ConfigData,
    defaultTier: 'fast' as AITier,
  },
  {
    task: 'comparison',
    label: 'Candidate Comparison',
    description: 'Compare candidate qualifications against job requirements',
    configKey: 'task_tier_comparison' as keyof ConfigData,
    defaultTier: 'standard' as AITier,
  },
  {
    task: 'reasoning',
    label: 'Deep Reasoning',
    description: 'Complex analysis requiring multi-step reasoning',
    configKey: 'task_tier_reasoning' as keyof ConfigData,
    defaultTier: 'standard' as AITier,
  },
  {
    task: 'audit',
    label: 'Quality Audit',
    description: 'Review and validate AI-generated assessments',
    configKey: 'task_tier_audit' as keyof ConfigData,
    defaultTier: 'premium' as AITier,
  },
] as const;

// Export for future use
void _TASK_TIER_MAPPINGS;

// US-237: Fallback pill options used while catalog is loading or unavailable (Epic 34)
const REVIEW_MODEL_FALLBACK_OPTIONS = [
  { label: 'Claude Sonnet', value: OPENROUTER_MODEL_IDS.claudeSonnet5 },
  { label: 'Claude Opus', value: OPENROUTER_MODEL_IDS.claudeOpus48 },
  { label: 'GPT-4o', value: OPENROUTER_MODEL_IDS.gpt4o },
];

function IntegrationsSettings({ config, onRefresh }: IntegrationsSettingsProps) {
  const [srApiKey, setSrApiKey] = useState('');
  const [openRouterApiKey, setOpenRouterApiKey] = useState('');
  const [aiModel, setAiModel] = useState(config.config.ai_model || OPENROUTER_MODEL_IDS.claudeSonnet5);
  // US-076: Tiered model configuration
  const [aiModelTierFast, setAiModelTierFast] = useState(config.config.ai_model_tier_fast || OPENROUTER_MODEL_IDS.deepseekChat);
  const [aiModelTierStandard, setAiModelTierStandard] = useState(config.config.ai_model_tier_standard || OPENROUTER_MODEL_IDS.claudeHaiku45);
  const [aiModelTierPremium, setAiModelTierPremium] = useState(config.config.ai_model_tier_premium || OPENROUTER_MODEL_IDS.gpt4o);

  // Auto-sync confidence threshold (0-50 scale, matches review overall_score)
  const [autoSyncThreshold, setAutoSyncThreshold] = useState<number>(
    config.config.auto_sync_threshold != null ? parseInt(config.config.auto_sync_threshold, 10) : 38
  );

  // US-237: Model catalog from API — drives ModelPillSelector; fallback to local constant while loading (Epic 34)
  const { data: catalogData } = useModelCatalog();
  const reviewModelOptions = catalogData?.models
    ? catalogData.models.map((m) => ({ label: m.displayName, value: m.modelId }))
    : REVIEW_MODEL_FALLBACK_OPTIONS;

  // US-078: Task-to-Tier configuration
  const [taskTierEmbedding, setTaskTierEmbedding] = useState<AITier>((config.config.task_tier_embedding as AITier) || 'fast');
  const [taskTierExtraction, setTaskTierExtraction] = useState<AITier>((config.config.task_tier_extraction as AITier) || 'fast');
  const [taskTierComparison, setTaskTierComparison] = useState<AITier>((config.config.task_tier_comparison as AITier) || 'standard');
  const [taskTierReasoning, setTaskTierReasoning] = useState<AITier>((config.config.task_tier_reasoning as AITier) || 'standard');
  const [taskTierAudit, setTaskTierAudit] = useState<AITier>((config.config.task_tier_audit as AITier) || 'premium');

  const [showSrKey, setShowSrKey] = useState(false);
  const [showOpenRouterKey, setShowOpenRouterKey] = useState(false);

  // Parse initial allowed stages from config (comma-separated string)
  const parseAllowedStages = (value: string | null | undefined): string[] => {
    if (!value) return ['NEW']; // Default to NEW only
    return value.split(',').map(s => s.trim().toUpperCase()).filter(s => s.length > 0);
  };

  const [allowedStages, setAllowedStages] = useState<string[]>(
    parseAllowedStages(config.config.default_allowed_stages)
  );

  const [srTestStatus, setSrTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [srTestError, setSrTestError] = useState<string | null>(null);
  const [openRouterTestStatus, setOpenRouterTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [openRouterTestError, setOpenRouterTestError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const hasExistingSrKey = config.config.sr_api_key && config.config.sr_api_key !== '********';
  const hasExistingOpenRouterKey = config.config.openrouter_api_key && config.config.openrouter_api_key !== '********';

  const testSmartRecruitersConnection = async () => {
    const keyToTest = srApiKey || '';
    if (!keyToTest && !hasExistingSrKey) {
      setSrTestError('Please enter an API key first');
      setSrTestStatus('error');
      return;
    }

    setSrTestStatus('testing');
    setSrTestError(null);

    try {
      const result = await api.testSmartRecruitersConnection(keyToTest);
      if (result.valid || result.results.sr_api_key?.valid) {
        setSrTestStatus('success');
      } else {
        setSrTestStatus('error');
        setSrTestError(result.results.sr_api_key?.error || 'Connection failed');
      }
    } catch (err) {
      setSrTestStatus('error');
      setSrTestError(err instanceof Error ? err.message : 'Test failed');
    }
  };

  const testOpenRouterConnection = async () => {
    const keyToTest = openRouterApiKey || '';
    if (!keyToTest && !hasExistingOpenRouterKey) {
      setOpenRouterTestError('Please enter an API key first');
      setOpenRouterTestStatus('error');
      return;
    }

    setOpenRouterTestStatus('testing');
    setOpenRouterTestError(null);

    try {
      const result = await api.testOpenRouterConnection(keyToTest);
      if (result.valid || result.results.openrouter_api_key?.valid) {
        setOpenRouterTestStatus('success');
      } else {
        setOpenRouterTestStatus('error');
        setOpenRouterTestError(result.results.openrouter_api_key?.error || 'Connection failed');
      }
    } catch (err) {
      setOpenRouterTestStatus('error');
      setOpenRouterTestError(err instanceof Error ? err.message : 'Test failed');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const updateData: ConfigData = {};

      if (srApiKey) {
        updateData.sr_api_key = srApiKey;
      }
      if (openRouterApiKey) {
        updateData.openrouter_api_key = openRouterApiKey;
      }
      if (aiModel !== config.config.ai_model) {
        updateData.ai_model = aiModel;
      }

      // US-076: Include tiered model config changes
      if (aiModelTierFast !== config.config.ai_model_tier_fast) {
        updateData.ai_model_tier_fast = aiModelTierFast;
      }
      if (aiModelTierStandard !== config.config.ai_model_tier_standard) {
        updateData.ai_model_tier_standard = aiModelTierStandard;
      }
      if (aiModelTierPremium !== config.config.ai_model_tier_premium) {
        updateData.ai_model_tier_premium = aiModelTierPremium;
      }

      // US-078: Include task-to-tier mapping changes
      if (taskTierEmbedding !== (config.config.task_tier_embedding || 'fast')) {
        updateData.task_tier_embedding = taskTierEmbedding;
      }
      if (taskTierExtraction !== (config.config.task_tier_extraction || 'fast')) {
        updateData.task_tier_extraction = taskTierExtraction;
      }
      if (taskTierComparison !== (config.config.task_tier_comparison || 'standard')) {
        updateData.task_tier_comparison = taskTierComparison;
      }
      if (taskTierReasoning !== (config.config.task_tier_reasoning || 'standard')) {
        updateData.task_tier_reasoning = taskTierReasoning;
      }
      if (taskTierAudit !== (config.config.task_tier_audit || 'premium')) {
        updateData.task_tier_audit = taskTierAudit;
      }

      const currentAllowedStages = parseAllowedStages(config.config.default_allowed_stages);
      if (allowedStages.join(',') !== currentAllowedStages.join(',')) {
        updateData.default_allowed_stages = allowedStages.join(',');
      }

      const originalThreshold = config.config.auto_sync_threshold != null
        ? parseInt(config.config.auto_sync_threshold, 10)
        : 38;
      if (autoSyncThreshold !== originalThreshold) {
        updateData.auto_sync_threshold = autoSyncThreshold.toString();
      }

      if (Object.keys(updateData).length === 0) {
        setSaveError('No changes to save');
        setSaving(false);
        return;
      }

      await api.saveConfig(updateData);
      setSaveSuccess(true);
      setSrApiKey('');
      setOpenRouterApiKey('');
      onRefresh();

      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setSrApiKey('');
    setOpenRouterApiKey('');
    setAiModel(config.config.ai_model || OPENROUTER_MODEL_IDS.claudeSonnet5);
    // US-076: Reset tiered model configs
    setAiModelTierFast(config.config.ai_model_tier_fast || OPENROUTER_MODEL_IDS.deepseekChat);
    setAiModelTierStandard(config.config.ai_model_tier_standard || OPENROUTER_MODEL_IDS.claudeHaiku45);
    setAiModelTierPremium(config.config.ai_model_tier_premium || OPENROUTER_MODEL_IDS.gpt4o);
    // US-078: Reset task-to-tier configs
    setTaskTierEmbedding((config.config.task_tier_embedding as AITier) || 'fast');
    setTaskTierExtraction((config.config.task_tier_extraction as AITier) || 'fast');
    setTaskTierComparison((config.config.task_tier_comparison as AITier) || 'standard');
    setTaskTierReasoning((config.config.task_tier_reasoning as AITier) || 'standard');
    setTaskTierAudit((config.config.task_tier_audit as AITier) || 'premium');
    setAllowedStages(parseAllowedStages(config.config.default_allowed_stages));
    setAutoSyncThreshold(
      config.config.auto_sync_threshold != null ? parseInt(config.config.auto_sync_threshold, 10) : 38
    );
    setSrTestStatus('idle');
    setSrTestError(null);
    setOpenRouterTestStatus('idle');
    setOpenRouterTestError(null);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const currentAllowedStages = parseAllowedStages(config.config.default_allowed_stages);

  // US-078: Helper to check task tier changes
  const hasTaskTierChanges =
    taskTierEmbedding !== (config.config.task_tier_embedding || 'fast') ||
    taskTierExtraction !== (config.config.task_tier_extraction || 'fast') ||
    taskTierComparison !== (config.config.task_tier_comparison || 'standard') ||
    taskTierReasoning !== (config.config.task_tier_reasoning || 'standard') ||
    taskTierAudit !== (config.config.task_tier_audit || 'premium');

  const originalThreshold = config.config.auto_sync_threshold != null
    ? parseInt(config.config.auto_sync_threshold, 10)
    : 38;

  const hasChanges = srApiKey || openRouterApiKey ||
    aiModel !== config.config.ai_model ||
    aiModelTierFast !== config.config.ai_model_tier_fast ||
    aiModelTierStandard !== config.config.ai_model_tier_standard ||
    aiModelTierPremium !== config.config.ai_model_tier_premium ||
    hasTaskTierChanges ||
    allowedStages.join(',') !== currentAllowedStages.join(',') ||
    autoSyncThreshold !== originalThreshold;

  return (
    <div className="space-y-6">
      {/* SmartRecruiters Integration */}
      <Card>
        <CardHeader
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                <Zap className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <span className="text-lg font-semibold">SmartRecruiters Integration</span>
                <p className="text-sm font-normal text-gray-500">ATS connection settings</p>
              </div>
            </div>
          }
          action={
            hasExistingSrKey ? (
              <Badge variant="success" dot>Connected</Badge>
            ) : (
              <Badge variant="warning" dot>Not Configured</Badge>
            )
          }
        />
        <CardBody className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Key
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 relative">
                <Input
                  type={showSrKey ? 'text' : 'password'}
                  value={srApiKey}
                  onChange={(e) => setSrApiKey(e.target.value)}
                  placeholder={hasExistingSrKey ? config.config.sr_api_key || '' : 'Enter SmartRecruiters API key'}
                  fullWidth
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowSrKey(!showSrKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSrKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button
                variant="secondary"
                onClick={testSmartRecruitersConnection}
                loading={srTestStatus === 'testing'}
                disabled={srTestStatus === 'testing'}
                className="w-full sm:w-auto"
              >
                {srTestStatus === 'success' ? (
                  <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
                ) : srTestStatus === 'error' ? (
                  <AlertCircle className="mr-2 h-4 w-4 text-red-600" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Test Connection
              </Button>
            </div>
            {srTestStatus === 'success' && (
              <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="h-4 w-4" />
                Connection successful
              </p>
            )}
            {srTestError && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {srTestError}
              </p>
            )}
          </div>

          <div className="pt-2 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              <strong>Note:</strong> To generate an API key, go to SmartRecruiters Admin Settings &gt; API &gt; API Keys.
            </p>
          </div>
        </CardBody>
      </Card>

      {/* AI / OpenRouter Integration */}
      <Card>
        <CardHeader
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
                <Brain className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <span className="text-lg font-semibold">AI Configuration</span>
                <p className="text-sm font-normal text-gray-500">OpenRouter API and model settings</p>
              </div>
            </div>
          }
          action={
            hasExistingOpenRouterKey ? (
              <Badge variant="success" dot>Connected</Badge>
            ) : (
              <Badge variant="warning" dot>Not Configured</Badge>
            )
          }
        />
        <CardBody className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              OpenRouter API Key
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 relative">
                <Input
                  type={showOpenRouterKey ? 'text' : 'password'}
                  value={openRouterApiKey}
                  onChange={(e) => setOpenRouterApiKey(e.target.value)}
                  placeholder={hasExistingOpenRouterKey ? config.config.openrouter_api_key || '' : 'Enter OpenRouter API key'}
                  fullWidth
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowOpenRouterKey(!showOpenRouterKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showOpenRouterKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button
                variant="secondary"
                onClick={testOpenRouterConnection}
                loading={openRouterTestStatus === 'testing'}
                disabled={openRouterTestStatus === 'testing'}
                className="w-full sm:w-auto"
              >
                {openRouterTestStatus === 'success' ? (
                  <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
                ) : openRouterTestStatus === 'error' ? (
                  <AlertCircle className="mr-2 h-4 w-4 text-red-600" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Test Connection
              </Button>
            </div>
            {openRouterTestStatus === 'success' && (
              <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="h-4 w-4" />
                Connection successful
              </p>
            )}
            {openRouterTestError && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {openRouterTestError}
              </p>
            )}
          </div>

          {/* Quick model picker — maps to standard tier */}
          <div className="pt-4 border-t border-gray-100 space-y-4">
            <div>
              <p className="text-sm font-medium text-stone-800 dark:text-stone-100 mb-1">Review model</p>
              <p className="text-xs text-stone-500 dark:text-stone-400 mb-2">The model Arbie uses to score candidates.</p>
              <ModelPillSelector
                value={aiModelTierStandard}
                onChange={(v) => setAiModelTierStandard(v)}
                options={reviewModelOptions}
              />
            </div>

            <ThresholdStepper
              value={autoSyncThreshold}
              onChange={setAutoSyncThreshold}
              min={0}
              max={50}
              label="Auto-sync confidence threshold"
              description="Reviews at or above this score post to the ATS without a human."
            />
          </div>

          {/* US-076: Tiered AI Model Selection - US-075: Enhanced responsive layout */}
          <div className="space-y-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-purple-500" />
              <div>
                <h3 className="text-sm font-medium text-gray-900">Tiered AI Model Configuration</h3>
                <p className="text-sm text-gray-500">
                  Choose different AI models for different tasks to balance cost and quality.
                </p>
              </div>
            </div>

            {/* US-076: Model Configuration - Responsive grid with cost info */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {/* Fast Tier */}
              <TierModelCard
                tierKey="fast"
                tierLabel="Fast Tier"
                tierBadge="F"
                tierDescription="Stage filtering, quick checks"
                borderColor="border-green-200"
                bgColor="bg-green-50/50"
                badgeBg="bg-green-100"
                badgeText="text-green-700"
                focusColor="focus:border-green-500 focus:ring-green-500"
                value={aiModelTierFast}
                onChange={setAiModelTierFast}
                models={MODEL_CATALOG}
                extraClasses=""
              />

              {/* Standard Tier */}
              <TierModelCard
                tierKey="standard"
                tierLabel="Standard Tier"
                tierBadge="S"
                tierDescription="Resume evaluation"
                borderColor="border-blue-200"
                bgColor="bg-blue-50/50"
                badgeBg="bg-blue-100"
                badgeText="text-blue-700"
                focusColor="focus:border-blue-500 focus:ring-blue-500"
                value={aiModelTierStandard}
                onChange={setAiModelTierStandard}
                models={MODEL_CATALOG}
                isDefault
                extraClasses=""
              />

              {/* Premium Tier */}
              <TierModelCard
                tierKey="premium"
                tierLabel="Premium Tier"
                tierBadge="P"
                tierDescription="Quality audit, detailed analysis"
                borderColor="border-purple-200"
                bgColor="bg-purple-50/50"
                badgeBg="bg-purple-100"
                badgeText="text-purple-700"
                focusColor="focus:border-purple-500 focus:ring-purple-500"
                value={aiModelTierPremium}
                onChange={setAiModelTierPremium}
                models={MODEL_CATALOG}
                extraClasses="md:col-span-2 xl:col-span-1"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default AI Review Eligibility
            </label>
            {/* US-075: Responsive checkbox grid with adequate touch targets */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 border border-gray-200 rounded-lg bg-gray-50">
              {Object.entries(SR_CANDIDATE_STAGES).map(([, stage]) => (
                <label
                  key={stage.value}
                  className="flex items-start gap-3 cursor-pointer p-2 rounded-lg hover:bg-gray-100 transition-colors min-h-[44px]"
                >
                  <input
                    type="checkbox"
                    checked={allowedStages.includes(stage.value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setAllowedStages([...allowedStages, stage.value]);
                      } else {
                        setAllowedStages(allowedStages.filter(s => s !== stage.value));
                      }
                    }}
                    className="mt-0.5 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-700 flex items-center gap-2 flex-wrap">
                      {stage.label}
                      {stage.value === 'NEW' && (
                        <Badge variant="info" size="sm">Default</Badge>
                      )}
                    </span>
                    <p className="text-xs text-gray-500 truncate">{stage.description}</p>
                  </div>
                </label>
              ))}
            </div>
            {allowedStages.length === 0 && (
              <p className="mt-2 text-sm text-amber-600 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                At least one stage should be selected
              </p>
            )}
            <p className="mt-2 text-sm text-gray-500">
              Controls which candidate stages trigger automatic AI review processing. Candidates in the selected stages will be automatically queued for AI review when synced or discovered.
            </p>
          </div>

          <div className="pt-2 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              <strong>Note:</strong> Get your API key from{' '}
              <a
                href="https://openrouter.ai/keys"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
              >
                OpenRouter <ExternalLink className="h-3 w-3" />
              </a>
            </p>
          </div>
        </CardBody>
      </Card>

      {/* US-078: Task-to-Tier Mapping Configuration */}
      <Card>
        <CardHeader
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-100">
                <Layers className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <span className="text-lg font-semibold">Task-to-Tier Mapping</span>
                <p className="text-sm font-normal text-gray-500">Configure which AI tier handles each task type</p>
              </div>
            </div>
          }
        />
        <CardBody className="space-y-4">
          <p className="text-sm text-gray-500">
            Map each AI task to a tier (Fast/Standard/Premium). This controls which model is used for each type of processing.
          </p>
          <TaskTierTable
            taskTierEmbedding={taskTierEmbedding}
            setTaskTierEmbedding={setTaskTierEmbedding}
            taskTierExtraction={taskTierExtraction}
            setTaskTierExtraction={setTaskTierExtraction}
            taskTierComparison={taskTierComparison}
            setTaskTierComparison={setTaskTierComparison}
            taskTierReasoning={taskTierReasoning}
            setTaskTierReasoning={setTaskTierReasoning}
            taskTierAudit={taskTierAudit}
            setTaskTierAudit={setTaskTierAudit}
            aiModelTierFast={aiModelTierFast}
            aiModelTierStandard={aiModelTierStandard}
            aiModelTierPremium={aiModelTierPremium}
          />
          <div className="pt-4 border-t border-gray-100">
            <div className="grid grid-cols-3 gap-2">
              <div className="flex items-center gap-2 rounded bg-green-50 px-2 py-1.5"><span className="h-2 w-2 rounded-full bg-green-500" /><span className="text-xs font-medium text-green-800">Fast</span></div>
              <div className="flex items-center gap-2 rounded bg-blue-50 px-2 py-1.5"><span className="h-2 w-2 rounded-full bg-blue-500" /><span className="text-xs font-medium text-blue-800">Standard</span></div>
              <div className="flex items-center gap-2 rounded bg-purple-50 px-2 py-1.5"><span className="h-2 w-2 rounded-full bg-purple-500" /><span className="text-xs font-medium text-purple-800">Premium</span></div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Save/Cancel Actions */}
      {(hasChanges || saveError || saveSuccess) && (
        <Card>
          <CardBody>
            {saveError && (
              <Alert variant="error" className="mb-4">
                {saveError}
              </Alert>
            )}
            {saveSuccess && (
              <Alert variant="success" className="mb-4">
                Configuration saved successfully! Workers will pick up the new config within 5 minutes.
              </Alert>
            )}
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
              <Button variant="secondary" onClick={handleCancel} disabled={saving} className="w-full sm:w-auto">
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                loading={saving}
                disabled={saving || !hasChanges}
                className="w-full sm:w-auto"
              >
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </Button>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

interface WebhookSettingsProps {
  config: ConfigResponse;
  onRefresh: () => void;
}

function WebhookSettings({ config, onRefresh }: WebhookSettingsProps) {
  const [copied, setCopied] = useState(false);
  const [webhookSecret, setWebhookSecret] = useState('');
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [webhookSecretSaving, setWebhookSecretSaving] = useState(false);
  const [webhookSecretSuccess, setWebhookSecretSuccess] = useState(false);
  const [webhookSecretError, setWebhookSecretError] = useState<string | null>(null);

  const hasExistingWebhookSecret = config.config.webhook_secret && config.config.webhook_secret !== '********';

  // Build the webhook URL from the current window location or env variable
  const baseUrl = import.meta.env.VITE_API_URL || window.location.origin;
  const webhookUrl = `${baseUrl}/webhooks/smartrecruiters`;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSaveWebhookSecret = async () => {
    setWebhookSecretSaving(true);
    setWebhookSecretError(null);
    setWebhookSecretSuccess(false);

    try {
      await api.saveConfig({ webhook_secret: webhookSecret });
      setWebhookSecretSuccess(true);
      setWebhookSecret('');
      onRefresh();
      setTimeout(() => setWebhookSecretSuccess(false), 3000);
    } catch (err) {
      setWebhookSecretError(err instanceof Error ? err.message : 'Failed to save webhook secret');
    } finally {
      setWebhookSecretSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                <Webhook className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <span className="text-lg font-semibold">Webhook URL</span>
                <p className="text-sm font-normal text-gray-500">Configure SmartRecruiters to send events to this URL</p>
              </div>
            </div>
          }
        />
        <CardBody className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Webhook Endpoint
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2">
                  <code className="flex-1 text-sm font-mono text-gray-800 break-all">
                    {webhookUrl}
                  </code>
                </div>
              </div>
              <Button variant="secondary" onClick={copyToClipboard} className="w-full sm:w-auto">
                {copied ? (
                  <>
                    <Check className="mr-2 h-4 w-4 text-green-600" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="mr-2 h-4 w-4" />
                    Copy
                  </>
                )}
              </Button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Webhook Secret
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 relative">
                <Input
                  type={showWebhookSecret ? 'text' : 'password'}
                  value={webhookSecret}
                  onChange={(e) => setWebhookSecret(e.target.value)}
                  placeholder={hasExistingWebhookSecret ? config.config.webhook_secret || '********' : 'Enter webhook secret'}
                  fullWidth
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showWebhookSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button
                variant="primary"
                onClick={handleSaveWebhookSecret}
                loading={webhookSecretSaving}
                disabled={webhookSecretSaving || !webhookSecret}
                className="w-full sm:w-auto"
              >
                <Save className="mr-2 h-4 w-4" />
                Save
              </Button>
            </div>
            {webhookSecretSuccess && (
              <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="h-4 w-4" />
                Webhook secret saved successfully
              </p>
            )}
            {webhookSecretError && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {webhookSecretError}
              </p>
            )}
            {hasExistingWebhookSecret && (
              <p className="mt-1 text-sm text-green-600">
                <CheckCircle className="h-3 w-3 inline mr-1" />
                Webhook secret is configured
              </p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              Used to verify webhook requests from SmartRecruiters. Match this with the secret configured in SmartRecruiters.
            </p>
          </div>
        </CardBody>
      </Card>

      {/* Setup Instructions */}
      <Card>
        <CardHeader
          title="Setup Instructions"
          description="Follow these steps to configure webhooks in SmartRecruiters"
        />
        <CardBody>
          <ol className="space-y-4 text-sm">
            <li className="flex gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-blue-800">
                1
              </span>
              <div>
                <p className="font-medium text-gray-900">Navigate to Admin Settings</p>
                <p className="text-gray-600">
                  In SmartRecruiters, go to <strong>Settings</strong> &gt; <strong>Admin</strong> &gt; <strong>Integrations</strong> &gt; <strong>Webhooks</strong>
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-blue-800">
                2
              </span>
              <div>
                <p className="font-medium text-gray-900">Create a New Webhook</p>
                <p className="text-gray-600">
                  Click <strong>Add Webhook</strong> and paste the webhook URL above.
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-blue-800">
                3
              </span>
              <div>
                <p className="font-medium text-gray-900">Select Events</p>
                <p className="text-gray-600">
                  Enable the following event types:
                </p>
                <ul className="mt-2 ml-4 list-disc text-gray-600">
                  <li><code className="text-xs bg-gray-100 px-1 py-0.5 rounded">application.created</code> - New application submitted</li>
                  <li><code className="text-xs bg-gray-100 px-1 py-0.5 rounded">application.status.updated</code> - Application stage changed</li>
                  <li><code className="text-xs bg-gray-100 px-1 py-0.5 rounded">job.created</code> - New job posted</li>
                  <li><code className="text-xs bg-gray-100 px-1 py-0.5 rounded">job.updated</code> - Job details updated</li>
                </ul>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-blue-800">
                4
              </span>
              <div>
                <p className="font-medium text-gray-900">Configure Security (Optional)</p>
                <p className="text-gray-600">
                  If you want to verify webhook signatures, add a webhook secret in the SmartRecruiters configuration and update the <strong>webhook_secret</strong> setting above to match.
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-blue-800">
                5
              </span>
              <div>
                <p className="font-medium text-gray-900">Save and Test</p>
                <p className="text-gray-600">
                  Save the webhook configuration and use SmartRecruiters' test feature to verify the connection is working.
                </p>
              </div>
            </li>
          </ol>
        </CardBody>
        <CardFooter align="left">
          <a
            href="https://dev.smartrecruiters.com/customer-api/webhooks/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
          >
            View SmartRecruiters Webhook Documentation <ExternalLink className="h-3 w-3" />
          </a>
        </CardFooter>
      </Card>
    </div>
  );
}

// US-066: Data Retention Policy Settings
interface DataRetentionSettingsProps {
  config: ConfigResponse;
  onRefresh: () => void;
}

// Default retention periods (in days)
const RETENTION_DEFAULTS = {
  retention_inactive_applications_days: 365,
  retention_pii_days: 180,
  retention_reviews_days: 365,
  retention_audit_days: 730,
} as const;

// Preset buttons for quick selection
const RETENTION_PRESETS = [30, 90, 180, 365] as const;

// Retention setting definitions
const RETENTION_FIELDS = [
  {
    key: 'retention_inactive_applications_days' as const,
    label: 'Application Data',
    description: 'How long to keep application records after the associated job is closed. Includes application metadata, screening answers, and status history.',
    icon: Layers,
    defaultDays: RETENTION_DEFAULTS.retention_inactive_applications_days,
    maxDays: 1095,
  },
  {
    key: 'retention_pii_days' as const,
    label: 'Candidate PII',
    description: 'How long to keep personally identifiable information (names, emails, phone numbers, addresses). GDPR requires minimizing PII retention.',
    icon: Shield,
    defaultDays: RETENTION_DEFAULTS.retention_pii_days,
    maxDays: 730,
  },
  {
    key: 'retention_reviews_days' as const,
    label: 'AI Review History',
    description: 'How long to keep AI-generated candidate reviews, scores, and recommendations. Older reviews will be permanently deleted.',
    icon: Brain,
    defaultDays: RETENTION_DEFAULTS.retention_reviews_days,
    maxDays: 1095,
  },
  {
    key: 'retention_audit_days' as const,
    label: 'Audit Logs',
    description: 'How long to keep system audit logs including data deletion records, configuration changes, and compliance events. Longer retention recommended for regulatory compliance.',
    icon: Database,
    defaultDays: RETENTION_DEFAULTS.retention_audit_days,
    maxDays: 2555,
  },
] as const;

type RetentionConfigKey = 'retention_inactive_applications_days' | 'retention_pii_days' | 'retention_reviews_days' | 'retention_audit_days';

function DataRetentionSettings({ config, onRefresh }: DataRetentionSettingsProps) {
  // Parse config values with defaults
  const parseRetentionDays = (value: string | null | undefined, defaultValue: number, maxDays: number = 2555): number => {
    if (!value) return defaultValue;
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? defaultValue : Math.max(7, Math.min(maxDays, parsed));
  };

  const parseRetentionEnabled = (value: string | null | undefined): boolean => {
    return value === 'true';
  };

  const [retentionEnabled, setRetentionEnabled] = useState(
    parseRetentionEnabled(config.config.retention_enabled)
  );
  const [retentionValues, setRetentionValues] = useState<Record<RetentionConfigKey, number>>({
    retention_inactive_applications_days: parseRetentionDays(
      config.config.retention_inactive_applications_days,
      RETENTION_DEFAULTS.retention_inactive_applications_days
    ),
    retention_pii_days: parseRetentionDays(
      config.config.retention_pii_days,
      RETENTION_DEFAULTS.retention_pii_days
    ),
    retention_reviews_days: parseRetentionDays(
      config.config.retention_reviews_days,
      RETENTION_DEFAULTS.retention_reviews_days
    ),
    retention_audit_days: parseRetentionDays(
      config.config.retention_audit_days,
      RETENTION_DEFAULTS.retention_audit_days
    ),
  });

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Helper to get original value from config
  const getOriginalValue = (key: RetentionConfigKey): number => {
    const field = RETENTION_FIELDS.find(f => f.key === key);
    const defaultVal = field?.defaultDays ?? 365;
    return parseRetentionDays(config.config[key], defaultVal);
  };

  // Check if there are any changes
  const hasChanges =
    retentionEnabled !== parseRetentionEnabled(config.config.retention_enabled) ||
    RETENTION_FIELDS.some(field => retentionValues[field.key] !== getOriginalValue(field.key));

  const updateRetentionValue = (key: RetentionConfigKey, value: number) => {
    setRetentionValues(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const updateData: ConfigData = {
        retention_enabled: retentionEnabled.toString(),
        retention_inactive_jobs_days: retentionValues.retention_inactive_applications_days.toString(),
        retention_inactive_applications_days: retentionValues.retention_inactive_applications_days.toString(),
        retention_pii_days: retentionValues.retention_pii_days.toString(),
        retention_reviews_days: retentionValues.retention_reviews_days.toString(),
        retention_audit_days: retentionValues.retention_audit_days.toString(),
      };

      await api.saveConfig(updateData);
      setSaveSuccess(true);
      onRefresh();

      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save retention policy');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setRetentionEnabled(parseRetentionEnabled(config.config.retention_enabled));
    setRetentionValues({
      retention_inactive_applications_days: getOriginalValue('retention_inactive_applications_days'),
      retention_pii_days: getOriginalValue('retention_pii_days'),
      retention_reviews_days: getOriginalValue('retention_reviews_days'),
      retention_audit_days: getOriginalValue('retention_audit_days'),
    });
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleResetToDefaults = () => {
    setRetentionEnabled(false);
    setRetentionValues({
      retention_inactive_applications_days: RETENTION_DEFAULTS.retention_inactive_applications_days,
      retention_pii_days: RETENTION_DEFAULTS.retention_pii_days,
      retention_reviews_days: RETENTION_DEFAULTS.retention_reviews_days,
      retention_audit_days: RETENTION_DEFAULTS.retention_audit_days,
    });
  };

  return (
    <div className="space-y-6">
      {/* GDPR Compliance Warning Banner */}
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <Shield className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-900">GDPR / CCPA Compliance Notice</h3>
            <p className="text-sm text-amber-800 mt-1">
              Under GDPR Article 5(1)(e) and CCPA, personal data must be kept only as long as necessary for the purposes
              for which it was collected. Configuring appropriate retention periods helps ensure compliance. Data beyond
              the retention period will be <strong>permanently and irreversibly deleted</strong> during the next scheduled purge.
            </p>
            <p className="text-sm text-amber-700 mt-2">
              Consult your Data Protection Officer or legal team before modifying these settings.
            </p>
          </div>
        </div>
      </div>

      {/* Main Policy Card */}
      <Card>
        <CardHeader
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <span className="text-lg font-semibold">Data Retention Policy</span>
                <p className="text-sm font-normal text-gray-500">Configure how long each type of data is kept before automatic deletion</p>
              </div>
            </div>
          }
          action={
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetToDefaults}
                className="text-xs"
              >
                <RefreshCw className="mr-1 h-3.5 w-3.5" />
                Reset to Defaults
              </Button>
              {retentionEnabled ? (
                <Badge variant="success" dot>Enabled</Badge>
              ) : (
                <Badge variant="warning" dot>Disabled</Badge>
              )}
            </div>
          }
        />
        <CardBody className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <label className="flex items-center justify-between cursor-pointer min-h-[48px]">
              <div className="flex-1 min-w-0 pr-4">
                <p className="font-medium text-gray-900 dark:text-gray-100">Enable Data Retention</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  When enabled, data will be automatically purged based on the configured retention periods.
                  This must be explicitly enabled to prevent accidental data loss.
                </p>
              </div>
              <input
                type="checkbox"
                checked={retentionEnabled}
                onChange={(e) => setRetentionEnabled(e.target.checked)}
                className="h-5 w-5 rounded border-gray-300 text-amber-600 focus:ring-amber-500 flex-shrink-0"
              />
            </label>
          </div>

          {/* Retention Period Settings */}
          <div className={`space-y-4 ${!retentionEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
            {RETENTION_FIELDS.map((field) => {
              const FieldIcon = field.icon;
              const currentValue = retentionValues[field.key];
              return (
                <div key={field.key} className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 dark:bg-gray-700 flex-shrink-0 mt-0.5">
                      <FieldIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                        {field.label}
                      </label>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        {field.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 ml-11">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">Delete after</span>
                      <input
                        type="number"
                        min={7}
                        max={field.maxDays}
                        value={currentValue}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);
                          if (!isNaN(val) && val >= 1) {
                            updateRetentionValue(field.key, Math.min(val, field.maxDays));
                          }
                        }}
                        disabled={!retentionEnabled}
                        className="w-20 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-100 px-3 py-2 text-sm text-center focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500 min-h-[40px]"
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-400">days</span>
                    </div>
                    {/* Preset buttons */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {RETENTION_PRESETS.filter(p => p <= field.maxDays).map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => updateRetentionValue(field.key, preset)}
                          disabled={!retentionEnabled}
                          className={`px-2.5 py-1 text-xs rounded-md border transition-colors min-h-[28px] ${
                            currentValue === preset
                              ? 'bg-amber-100 border-amber-300 text-amber-800 font-medium dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300'
                              : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:border-gray-500'
                          }`}
                        >
                          {preset}d
                        </button>
                      ))}
                      {field.key === 'retention_audit_days' && (
                        <button
                          type="button"
                          onClick={() => updateRetentionValue(field.key, 730)}
                          disabled={!retentionEnabled}
                          className={`px-2.5 py-1 text-xs rounded-md border transition-colors min-h-[28px] ${
                            currentValue === 730
                              ? 'bg-amber-100 border-amber-300 text-amber-800 font-medium'
                              : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                          }`}
                        >
                          730d
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Policy Summary */}
          <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4 border border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Current Policy Summary</h4>
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
              <li className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full flex-shrink-0 ${retentionEnabled ? 'bg-green-500' : 'bg-gray-400'}`} />
                <span>Data retention is <strong>{retentionEnabled ? 'enabled' : 'disabled'}</strong></span>
              </li>
              {retentionEnabled && RETENTION_FIELDS.map((field) => (
                <li key={field.key} className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-amber-500 flex-shrink-0" />
                  <span>{field.label} deleted after <strong>{retentionValues[field.key]} days</strong></span>
                </li>
              ))}
            </ul>
          </div>

          {/* Compliance Note */}
          <div className="text-sm text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-4">
            <p>
              <strong>Defaults:</strong> Application data = 365 days, Candidate PII = 180 days, AI reviews = 365 days, Audit logs = 730 days.
              Changes take effect on the next scheduled purge cycle (runs daily at 3:00 AM UTC).
            </p>
          </div>
        </CardBody>
      </Card>

      {/* Save/Cancel Actions */}
      {(hasChanges || saveError || saveSuccess) && (
        <Card>
          <CardBody>
            {saveError && (
              <Alert variant="error" className="mb-4">
                {saveError}
              </Alert>
            )}
            {saveSuccess && (
              <Alert variant="success" className="mb-4">
                <CheckCircle className="inline h-4 w-4 mr-1" />
                Retention policy saved successfully!
              </Alert>
            )}
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
              <Button variant="secondary" onClick={handleCancel} disabled={saving} className="w-full sm:w-auto">
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                loading={saving}
                disabled={saving || !hasChanges}
                className="w-full sm:w-auto"
              >
                <Save className="mr-2 h-4 w-4" />
                Save Retention Policy
              </Button>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function GeneralSettings() {
  return (
    <Card>
      <CardHeader
        title="General Settings"
        description="Configure basic application settings"
      />
      <CardBody className="space-y-6">
        {/* US-075: Responsive form grid - 1 col on mobile, 2 on tablet+ */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Application Name
            </label>
            <Input
              type="text"
              defaultValue="Recruiter Buddy"
              fullWidth
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Default Timezone
            </label>
            {/* US-075: min-h-[44px] ensures adequate touch target */}
            <select className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]">
              <option>UTC</option>
              <option>America/New_York</option>
              <option>America/Los_Angeles</option>
              <option>Europe/London</option>
            </select>
          </div>
        </div>

        {/* US-075: Enhanced checkbox with better touch target */}
        <div>
          <label className="flex items-center gap-3 cursor-pointer p-2 -ml-2 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]">
            <input type="checkbox" className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" defaultChecked />
            <span className="text-sm text-gray-700">
              Enable automatic job syncing
            </span>
          </label>
        </div>
      </CardBody>
      {/* US-075: Full-width button on mobile */}
      <CardFooter>
        <Button variant="primary" className="w-full sm:w-auto">
          <Save className="mr-2 h-4 w-4" />
          Save Changes
        </Button>
      </CardFooter>
    </Card>
  );
}

// ── Dead Letter Queue filter options ──
const dlqResolvedOptions: FilterOption[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'retried', label: 'Retried' },
  { value: 'ignored', label: 'Ignored' },
  { value: 'fixed', label: 'Fixed' },
];

const dlqQueueOptions: FilterOption[] = [
  { value: 'enrich-candidate', label: 'Enrich Candidate' },
  { value: 'process-ai-review', label: 'AI Review' },
  { value: 'sync-review-to-sr', label: 'Sync Review' },
  { value: 'coordinator-check', label: 'Coordinator Check' },
  { value: 'candidate-sync', label: 'Candidate Sync' },
];

const dlqStatusColors: Record<DeadLetterResolvedStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  retried: 'bg-blue-100 text-blue-800',
  ignored: 'bg-gray-100 text-gray-800',
  fixed: 'bg-green-100 text-green-800',
};

function SystemHealthSettings() {
  const navigate = useNavigate();
  const { data: health, isLoading: healthLoading, error: healthError, refetch: refetchHealth } = useSystemHealth();

  // DLQ state
  const [dlqSearch, setDlqSearch] = useState('');
  const [dlqResolved, setDlqResolved] = useState('');
  const [dlqQueue, setDlqQueue] = useState('');
  const [dlqPage, setDlqPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const dlqLimit = 15;

  const { data: dlqData, isLoading: dlqLoading, refetch: refetchDlq } = useDeadLetterEntries({
    page: dlqPage,
    limit: dlqLimit,
    resolved: dlqResolved || undefined,
    queue: dlqQueue || undefined,
    search: dlqSearch || undefined,
  });

  const { data: dlqStats } = useDeadLetterStats();
  const resolveMutation = useResolveDeadLetter();

  const unresolvedCount = dlqStats?.byStatus?.pending ?? 0;
  const hasActiveDlqFilters = dlqSearch || dlqResolved || dlqQueue;

  const handleBulkResolve = async (status: string) => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Mark ${selectedIds.size} entries as "${status}"?`)) return;
    try {
      await Promise.all(
        Array.from(selectedIds).map((id) => resolveMutation.mutateAsync({ id, resolved: status }))
      );
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Bulk resolve failed:', err);
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!dlqData?.data) return;
    setSelectedIds(
      selectedIds.size === dlqData.data.length ? new Set() : new Set(dlqData.data.map((e) => e.id))
    );
  };

  // ── Health helpers ──
  const getStatusColor = (status: 'up' | 'down' | undefined) => {
    if (status === 'up') return 'bg-green-500';
    if (status === 'down') return 'bg-red-500';
    return 'bg-gray-400';
  };

  const getOverallStatusColor = (status: string | undefined) => {
    if (status === 'healthy') return 'bg-green-100 text-green-800 border-green-200';
    if (status === 'degraded') return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    if (status === 'unhealthy') return 'bg-red-100 text-red-800 border-red-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getOverallStatusLabel = (status: string | undefined) => {
    if (status === 'healthy') return 'All Systems Operational';
    if (status === 'degraded') return 'Degraded Performance';
    if (status === 'unhealthy') return 'System Issues Detected';
    return 'Unknown';
  };

  const formatLatency = (latency: number | undefined) => {
    return latency === undefined ? 'N/A' : `${latency}ms`;
  };

  const queueTotals = (() => {
    if (!health?.services?.queues) return { waiting: 0, active: 0, failed: 0 };
    const queues = health.services.queues;
    return {
      waiting: Object.values(queues).reduce((sum, q) => sum + q.waiting, 0),
      active: Object.values(queues).reduce((sum, q) => sum + q.active, 0),
      failed: Object.values(queues).reduce((sum, q) => sum + q.failed, 0),
    };
  })();

  return (
    <div className="space-y-6">
      {/* ── Infrastructure Health ── */}
      <Card>
        <CardHeader
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Database className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <span className="text-lg font-semibold">Infrastructure</span>
                <p className="text-sm font-normal text-gray-500 dark:text-gray-400">Services, databases & worker queues</p>
              </div>
            </div>
          }
          action={
            healthLoading ? (
              <Spinner size="sm" />
            ) : (
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium ${getOverallStatusColor(health?.status)}`}>
                  {getOverallStatusLabel(health?.status)}
                </span>
                <Button variant="secondary" size="sm" onClick={() => refetchHealth()}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            )
          }
        />
        {healthError ? (
          <CardBody>
            <Alert variant="error" title="Failed to load system health">
              {healthError instanceof Error ? healthError.message : 'Unknown error'}
              <Button variant="secondary" size="sm" onClick={() => refetchHealth()} className="mt-3">
                <RefreshCw className="mr-2 h-4 w-4" /> Retry
              </Button>
            </Alert>
          </CardBody>
        ) : !healthLoading && health ? (
          <CardBody className="space-y-6">
            {/* Core Services */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`h-2.5 w-2.5 rounded-full ${getStatusColor(health.services?.database?.status)}`} />
                    <span className="font-medium text-gray-900 dark:text-gray-100">PostgreSQL</span>
                  </div>
                  <span className={`text-sm ${health.services?.database?.status === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                    {health.services?.database?.status === 'up' ? 'Connected' : 'Disconnected'}
                  </span>
                </div>
                <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Latency: {formatLatency(health.services?.database?.latency)}
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`h-2.5 w-2.5 rounded-full ${getStatusColor(health.services?.redis?.status)}`} />
                    <span className="font-medium text-gray-900 dark:text-gray-100">Redis</span>
                  </div>
                  <span className={`text-sm ${health.services?.redis?.status === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                    {health.services?.redis?.status === 'up' ? 'Connected' : 'Disconnected'}
                  </span>
                </div>
                <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Latency: {formatLatency(health.services?.redis?.latency)}
                </div>
              </div>
            </div>

            {/* Queue Summary */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Worker Queues</h3>
              <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4">
                <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 p-3 text-center">
                  <div className="text-xl font-bold text-blue-700 dark:text-blue-300">{queueTotals.waiting}</div>
                  <div className="text-xs text-blue-600 dark:text-blue-400">Waiting</div>
                </div>
                <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-3 text-center">
                  <div className="text-xl font-bold text-green-700 dark:text-green-300">{queueTotals.active}</div>
                  <div className="text-xs text-green-600 dark:text-green-400">Active</div>
                </div>
                <div className={`rounded-lg p-3 text-center ${queueTotals.failed > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-gray-50 dark:bg-gray-800'}`}>
                  <div className={`text-xl font-bold ${queueTotals.failed > 0 ? 'text-red-700 dark:text-red-300' : 'text-gray-700 dark:text-gray-300'}`}>{queueTotals.failed}</div>
                  <div className={`text-xs ${queueTotals.failed > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}>Failed</div>
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Queue</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Waiting</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Active</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Failed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
                    {health.services?.queues && Object.entries(health.services.queues).map(([name, counts]) => (
                      <tr key={name}>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">{name.replace(/([A-Z])/g, ' $1').trim()}</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-500 dark:text-gray-400">{counts.waiting}</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-500 dark:text-gray-400">{counts.active}</td>
                        <td className={`px-4 py-2 text-sm text-right ${counts.failed > 0 ? 'text-red-600 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>{counts.failed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 pt-4 border-t border-gray-200 dark:border-gray-700">
              <span>Version: {health.version || 'Unknown'}</span>
              <span>Last updated: {health.timestamp ? new Date(health.timestamp).toLocaleTimeString() : 'N/A'}</span>
            </div>
          </CardBody>
        ) : null}
      </Card>

      {/* ── Dead Letter Queue ── */}
      <Card>
        <CardHeader
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <span className="text-lg font-semibold">Error Queue</span>
                <p className="text-sm font-normal text-gray-500">
                  {dlqData?.pagination.total ?? 0} total entries
                  {unresolvedCount > 0 && ` (${unresolvedCount} unresolved)`}
                </p>
              </div>
            </div>
          }
          action={
            <Button variant="secondary" size="sm" onClick={() => refetchDlq()} loading={dlqLoading}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          }
        />
        <CardBody className="space-y-4">
          {/* Stats Summary */}
          {dlqStats && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-lg border border-gray-200 p-3 text-center">
                <div className="text-xl font-bold text-gray-900">{dlqStats.total}</div>
                <div className="text-xs text-gray-500">Total</div>
              </div>
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-center">
                <div className="text-xl font-bold text-yellow-700">{unresolvedCount}</div>
                <div className="text-xs text-yellow-600">Unresolved</div>
              </div>
              {Object.entries(dlqStats.byQueue).slice(0, 2).map(([qName, count]) => (
                <div key={qName} className="rounded-lg border border-gray-200 p-3 text-center">
                  <div className="text-xl font-bold text-gray-900">{count}</div>
                  <div className="text-xs text-gray-500 truncate" title={qName}>{qName}</div>
                </div>
              ))}
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px]">
              <SearchInput
                value={dlqSearch}
                onChange={(value) => { setDlqSearch(value); setDlqPage(1); }}
                placeholder="Search errors..."
                label="Search"
                isLoading={dlqLoading}
              />
            </div>
            <FilterDropdown
              label="Status"
              options={dlqResolvedOptions}
              value={dlqResolved}
              onChange={(value) => { setDlqResolved(value); setDlqPage(1); }}
              allOptionLabel="All"
              size="sm"
            />
            <FilterDropdown
              label="Queue"
              options={dlqQueueOptions}
              value={dlqQueue}
              onChange={(value) => { setDlqQueue(value); setDlqPage(1); }}
              allOptionLabel="All"
              size="sm"
            />
            {hasActiveDlqFilters && (
              <button onClick={() => { setDlqSearch(''); setDlqResolved(''); setDlqQueue(''); setDlqPage(1); }} className="text-sm text-gray-500 hover:text-gray-700">
                Clear
              </button>
            )}
          </div>

          {/* Bulk Actions */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2">
              <span className="text-sm font-medium text-blue-800">{selectedIds.size} selected</span>
              <Button variant="primary" size="sm" onClick={() => handleBulkResolve('fixed')} loading={resolveMutation.isPending}>
                <CheckCircle className="mr-1 h-3 w-3" /> Mark Fixed
              </Button>
              <Button variant="secondary" size="sm" onClick={() => handleBulkResolve('ignored')} loading={resolveMutation.isPending}>
                Mark Ignored
              </Button>
              <button onClick={() => setSelectedIds(new Set())} className="text-xs text-gray-500 hover:text-gray-700 ml-auto">
                Clear
              </button>
            </div>
          )}

          {/* Table */}
          <div className="rounded-lg border border-gray-200 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2.5 text-left w-8">
                    <input
                      type="checkbox"
                      checked={dlqData?.data.length ? selectedIds.size === dlqData.data.length : false}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                  </th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">Queue</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">Error</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">Failed</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {dlqLoading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center">
                      <RefreshCw className="mx-auto h-6 w-6 animate-spin text-gray-400" />
                      <p className="mt-1 text-sm text-gray-500">Loading...</p>
                    </td>
                  </tr>
                ) : dlqData?.data.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center">
                      {hasActiveDlqFilters ? (
                        <>
                          <Search className="mx-auto h-6 w-6 text-gray-400" />
                          <p className="mt-1 text-sm text-gray-500">No entries match filters</p>
                        </>
                      ) : (
                        <>
                          <Inbox className="mx-auto h-6 w-6 text-gray-400" />
                          <p className="mt-1 text-sm text-gray-500">No dead letter entries</p>
                        </>
                      )}
                    </td>
                  </tr>
                ) : (
                  dlqData?.data.map((entry) => (
                    <tr
                      key={entry.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/dead-letter/${entry.id}`)}
                    >
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(entry.id)}
                          onChange={() => toggleSelect(entry.id)}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
                          {entry.originalQueue}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <p className="max-w-xs truncate text-sm text-red-600" title={entry.error || ''}>
                          {entry.error || 'No error message'}
                        </p>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {formatDistanceToNow(new Date(entry.failedAt), { addSuffix: true })}
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${dlqStatusColors[entry.resolved as DeadLetterResolvedStatus] || dlqStatusColors.pending}`}>
                          {entry.resolved}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {dlqData && dlqData.pagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-2">
                <div className="text-xs text-gray-500">
                  {(dlqPage - 1) * dlqLimit + 1}-{Math.min(dlqPage * dlqLimit, dlqData.pagination.total)} of {dlqData.pagination.total}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setDlqPage(Math.max(1, dlqPage - 1))}
                    disabled={dlqPage === 1}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 disabled:opacity-50"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setDlqPage(Math.min(dlqData.pagination.totalPages, dlqPage + 1))}
                    disabled={dlqPage === dlqData.pagination.totalPages}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 disabled:opacity-50"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

const NOTIFICATION_PREFS_KEY = 'rb_notification_prefs';

function loadNotificationPrefs() {
  try {
    const raw = localStorage.getItem(NOTIFICATION_PREFS_KEY);
    if (raw) return JSON.parse(raw) as { newApplications: boolean; processingErrors: boolean; dailySummary: boolean };
  } catch { /* ignore */ }
  return { newApplications: true, processingErrors: true, dailySummary: false };
}

function NotificationsSettings() {
  const [prefs, setPrefs] = useState(loadNotificationPrefs);
  const [saved, setSaved] = useState(false);

  const toggle = (key: keyof typeof prefs) => {
    setPrefs(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(NOTIFICATION_PREFS_KEY, JSON.stringify(next));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      return next;
    });
  };

  return (
    <Card>
      <CardHeader
        title="Notifications"
        description="Configure how you receive updates"
      />
      <CardBody className="space-y-3">
        {saved && (
          <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-2 text-sm text-green-700">
            <CheckCircle className="h-4 w-4 flex-shrink-0" />
            Preferences saved
          </div>
        )}
        {/* US-075: Enhanced notification toggles with better touch targets */}
        <label className="flex items-center justify-between rounded-lg border border-gray-200 p-4 cursor-pointer hover:bg-gray-50 transition-colors min-h-[72px]">
          <div className="flex-1 min-w-0 pr-4">
            <p className="font-medium text-gray-900">New Applications</p>
            <p className="text-sm text-gray-500">
              Get notified when new applications are received
            </p>
          </div>
          <input
            type="checkbox"
            className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
            checked={prefs.newApplications}
            onChange={() => toggle('newApplications')}
          />
        </label>

        <label className="flex items-center justify-between rounded-lg border border-gray-200 p-4 cursor-pointer hover:bg-gray-50 transition-colors min-h-[72px]">
          <div className="flex-1 min-w-0 pr-4">
            <p className="font-medium text-gray-900">Processing Errors</p>
            <p className="text-sm text-gray-500">
              Get notified when processing fails
            </p>
          </div>
          <input
            type="checkbox"
            className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
            checked={prefs.processingErrors}
            onChange={() => toggle('processingErrors')}
          />
        </label>

        <label className="flex items-center justify-between rounded-lg border border-gray-200 p-4 cursor-pointer hover:bg-gray-50 transition-colors min-h-[72px]">
          <div className="flex-1 min-w-0 pr-4">
            <p className="font-medium text-gray-900">Daily Summary</p>
            <p className="text-sm text-gray-500">
              Receive a daily email summary
            </p>
          </div>
          <input
            type="checkbox"
            className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
            checked={prefs.dailySummary}
            onChange={() => toggle('dailySummary')}
          />
        </label>
      </CardBody>
    </Card>
  );
}

function SecuritySettings() {
  return (
    <Card>
      <CardHeader
        title="Security"
        description="Manage authentication and access control"
      />
      <CardBody className="space-y-6">
        {/* US-075: API Keys section */}
        <div>
          <h3 className="font-medium text-gray-900">API Keys</h3>
          <p className="text-sm text-gray-500">
            Manage API keys for external access
          </p>
          <Button variant="secondary" className="mt-3 w-full sm:w-auto">Generate New Key</Button>
        </div>

        {/* US-075: Session Settings with responsive input */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="font-medium text-gray-900">Session Settings</h3>
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Session Timeout (minutes)
            </label>
            {/* US-075: Wider input on mobile for better touch interaction */}
            <Input type="number" defaultValue={60} className="w-full sm:w-32" />
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
